import { useState, useEffect } from 'react';
import { BarChart2, TrendingUp } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend,
} from 'recharts';
import { getDjEventAnalytics } from '../../services/analytics';
import type { EventAnalytics } from '../../types/analytics';

interface Props {
  currentAnalytics: EventAnalytics | null;
  djId: string;
  currentEventId: string;
}

export default function ComparativeAnalytics({ currentAnalytics, djId, currentEventId }: Props) {
  const [historicalData, setHistoricalData] = useState<EventAnalytics[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const all = await getDjEventAnalytics(djId);
        // Exclude current event, sort by date
        const historical = all
          .filter(a => a.eventId !== currentEventId)
          .sort((a, b) => a.eventDate.localeCompare(b.eventDate));
        setHistoricalData(historical);
      } catch (error) {
        console.error('[Analytics] Loading past events failed:', error);
        setLoadFailed(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [djId, currentEventId]);

  if (loading) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
        <div className="animate-pulse text-gray-500">Loading comparative data...</div>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <BarChart2 className="w-5 h-5 text-primary" />
          Cross-Event Comparison
        </h3>
        <div className="h-32 flex items-center justify-center text-gray-500">
          <p>Couldn't load your past events. Reload the page to try again.</p>
        </div>
      </div>
    );
  }

  if (historicalData.length === 0) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <BarChart2 className="w-5 h-5 text-primary" />
          Cross-Event Comparison
        </h3>
        <div className="h-32 flex items-center justify-center text-gray-500">
          <p>Complete 2+ events to see comparative analytics</p>
        </div>
      </div>
    );
  }

  // Comparison: current event vs averages
  const avg = (field: (a: EventAnalytics) => number) => {
    const values = historicalData.map(field);
    return values.length > 0 ? Math.round(values.reduce((a, b) => a + b, 0) / values.length) : 0;
  };

  const comparisonData = currentAnalytics ? [
    { metric: 'Requests', current: currentAnalytics.summary.totalRequests, average: avg(a => a.summary.totalRequests) },
    { metric: 'Votes', current: currentAnalytics.summary.totalVotes, average: avg(a => a.summary.totalVotes) },
    { metric: 'Attendees', current: currentAnalytics.summary.uniqueAttendees, average: avg(a => a.summary.uniqueAttendees) },
    { metric: 'Success %', current: currentAnalytics.queue.requestSuccessRate, average: avg(a => a.queue.requestSuccessRate) },
    { metric: 'Streams', current: currentAnalytics.discovery.estimatedStreams, average: avg(a => a.discovery.estimatedStreams) },
  ] : [];

  // Trend data: all events over time (including current)
  const allEvents = [...historicalData, ...(currentAnalytics ? [currentAnalytics] : [])]
    .sort((a, b) => a.eventDate.localeCompare(b.eventDate));

  const trendData = allEvents.map(a => ({
    name: a.eventName.length > 15 ? a.eventName.substring(0, 15) + '...' : a.eventName,
    date: a.eventDate,
    requests: a.summary.totalRequests,
    votes: a.summary.totalVotes,
    attendees: a.summary.uniqueAttendees,
  }));

  return (
    <div className="space-y-6">
      {/* Comparison Bar Chart */}
      {comparisonData.length > 0 && (
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <BarChart2 className="w-5 h-5 text-primary" />
            This Event vs Average
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={comparisonData} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis dataKey="metric" tick={{ fontSize: 11, fill: '#9CA3AF' }} />
              <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#F3F4F6',
                  fontSize: 12,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="current" fill="#6366F1" name="This Event" radius={[4, 4, 0, 0]} />
              <Bar dataKey="average" fill="#9CA3AF" name="DJ Average" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Trend Line Chart */}
      {trendData.length > 1 && (
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Event Trends ({trendData.length} events)
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={trendData} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9CA3AF' }} />
              <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#F3F4F6',
                  fontSize: 12,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="requests" stroke="#6366F1" strokeWidth={2} name="Requests" />
              <Line type="monotone" dataKey="votes" stroke="#10B981" strokeWidth={2} name="Votes" />
              <Line type="monotone" dataKey="attendees" stroke="#F59E0B" strokeWidth={2} name="Attendees" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
